import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  useWindowDimensions,
  Animated,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import LottieView from 'react-native-web-lottie';
import { useChat } from '@livekit/components-react';
import World from 'components/World';
import { LowMinutesPill } from 'components/subscription/WorldNotices';
import { useRemScale } from 'lib/remScale';
import {
  TUTOR_INPUT_BOTTOM_REM,
  TUTOR_INPUT_SIDE_PADDING_REM,
  TUTOR_INPUT_MAX_WIDTH,
  TUTOR_INPUT_OUTER_HEIGHT_REM,
} from './tutorUiLayout';

import { TYPE, FONTS } from 'style/typography';
import { on as onFirstVisit } from 'firstVisit/events';
import { SIMPLIFIED_FIRST_VISIT } from 'config/app.config';
import { setChipsHeight } from './tutorLayout';

// Sol v3 Q5 / onboarding-leftovers L4 A: the state tab is larger and eases in on every change, and the bar's border
// takes the state's colour (prototype STATES). Off in production until the simplified first visit rolls out.
const VIVID = SIMPLIFIED_FIRST_VISIT;
const STATE_BORDER = {
  connecting: 'rgba(160,178,204,0.55)',
  speaking: 'rgba(124,193,255,0.65)',
  thinking: 'rgba(255,194,75,0.7)',
  prompt: 'rgba(197,232,54,0.75)',
};

/** Eases its children in (fade and rise) when it mounts; keyed by the state, it replays on every change. */
function StateTabMotion({ children }) {
  const shown = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(shown, { toValue: 1, duration: 180, useNativeDriver: Platform.OS !== 'web' }).start();
  }, [shown]);
  return (
    <Animated.View
      pointerEvents="box-none"
      style={{ opacity: shown, transform: [{ translateY: shown.interpolate({ inputRange: [0, 1], outputRange: [8, 0] }) }] }}>
      {children}
    </Animated.View>
  );
}

const PROMPT_TAB_ACCENT_COLOR = '#D7F46E';
const PROMPT_TAB_LOTTIE_COLOR_FILTERS = [
  {keypath: 'Fill 1', color: PROMPT_TAB_ACCENT_COLOR},
  {keypath: 'Stroke 1', color: PROMPT_TAB_ACCENT_COLOR},
];

export default function LiveKitTextInput({
  status = 'prompt',
  showMicPermissionWarning = false,
  // Minutes left this month on this mode, from the tutor's live meter (owner item 11).
  timeLeftLabel = null,
  // Running low: {label, onPress} turns the label into "2 min left | Upgrade" (owner 2026-09-29).
  lowMinutesAction = null,
  // Text mode: the microphone is a convenience, typing always works (owner item 9).
  micOptional = false,
  // {label, icon, onPress}: "Type instead" / "Talk instead" (simplified first visit), above the bar on the left.
  modeSwitch = null,
  // Up to three questions the learner can tap on their turn; a tap asks it as if typed (Sol onboarding-leftovers S2 A).
  suggestions = null,
  // The learner's microphone is on in a voice session: the hints read "Try saying:" (owner 11 Oct, voice-first).
  voiceLive = false,
  onMessageSent,
  onActivity,
}) {
  const [textMessage, setTextMessage] = useState('');
  const { send, isSending } = useChat();
  const [isHoveredSend, setIsHoveredSend] = useState(false);
  const lastSendAtRef = useRef(0);
  const showChips = Boolean(suggestions && suggestions.length && status === 'prompt' && !textMessage.trim());
  useEffect(() => {
    if (!showChips) setChipsHeight(0);
  }, [showChips]);
  useEffect(() => () => setChipsHeight(0), []);
  const { rem } = useRemScale();
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const isCompactViewport = viewportWidth <= 1180 || viewportHeight <= 760;

  // Input only accepts typing on the user's idle turn.
  const effectiveDisabled = isSending || status !== 'prompt';

  // The guided first visit's suggested questions: tapping one asks it, exactly as if it had been typed.
  const pendingAsk = useRef(null);
  useEffect(() => onFirstVisit('tutor-ask', question => {
    pendingAsk.current = String(question || '').trim() || null;
    if (pendingAsk.current) setTextMessage(pendingAsk.current);
  }), []);

  // memoize send handler, and notify parent to disable after send
  const onSend = useCallback(() => {
    if (effectiveDisabled) return;
    const msg = textMessage.trim();
    if (!msg) return;
    // Guard against accidental double-firing (e.g. keydown repeat / duplicate events).
    const now = Date.now();
    if (now - lastSendAtRef.current < 250) return;
    lastSendAtRef.current = now;
    console.log('[LiveKitTextInput] sending (explicit)', { length: msg.length });
    send(msg);
    setTextMessage('');
    if (typeof onMessageSent === 'function') {
      onMessageSent(msg);
    }
    World.runCommand(
      `livekit_final_response dummy ${msg.replace(/ /g, "_")} true player`
    );

  }, [effectiveDisabled, textMessage, send, onMessageSent]);

  // A suggested question is sent once the input holds it and it is the learner's turn.
  useEffect(() => {
    if (!pendingAsk.current || effectiveDisabled || textMessage.trim() !== pendingAsk.current) return;
    pendingAsk.current = null;
    onSend();
  }, [effectiveDisabled, onSend, textMessage]);

  // web-only hover handlers
  const hoverProps = Platform.OS === 'web' && !effectiveDisabled
    ? {
        onMouseEnter: () => setIsHoveredSend(true),
        onMouseLeave: () => setIsHoveredSend(false),
      }
    : {};

  // Web: `onKeyPress` is unreliable in react-native-web and can fire oddly with IME/composition.
  // Use `onKeyDown` and ONLY send on explicit Enter.
  const isExplicitEnter = useCallback((e) => {
    const ne = e?.nativeEvent;
    const key = ne?.key ?? e?.key;
    const keyCode = ne?.keyCode ?? e?.keyCode ?? ne?.which ?? e?.which;
    const isEnter = key === 'Enter' || keyCode === 13;
    if (!isEnter) return false;

    const isComposing = Boolean(ne?.isComposing ?? e?.isComposing);
    if (isComposing) return false;

    const repeat = Boolean(ne?.repeat ?? e?.repeat);
    if (repeat) return false;

    const shiftKey = Boolean(ne?.shiftKey ?? e?.shiftKey);
    const ctrlKey = Boolean(ne?.ctrlKey ?? e?.ctrlKey);
    const metaKey = Boolean(ne?.metaKey ?? e?.metaKey);
    const altKey = Boolean(ne?.altKey ?? e?.altKey);
    if (shiftKey || ctrlKey || metaKey || altKey) return false;

    return true;
  }, []);

  const onKeyDown = useCallback(
    e => {
      if (effectiveDisabled) return;
      if (!isExplicitEnter(e)) return;

      e?.preventDefault?.();
      e?.stopPropagation?.();
      onSend();
    },
    [effectiveDisabled, onSend, isExplicitEnter],
  );

  // Fallback for some react-native-web builds where keydown doesn't propagate as expected.
  const onKeyPress = useCallback(
    e => {
      if (effectiveDisabled) return;
      if (!isExplicitEnter(e)) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();
      onSend();
    },
    [effectiveDisabled, onSend, isExplicitEnter],
  );

  return (
    <View
      style={[
        styles.container,
        {
          bottom: rem(TUTOR_INPUT_BOTTOM_REM),
          paddingHorizontal: rem(TUTOR_INPUT_SIDE_PADDING_REM),
        },
      ]}>
      <View style={styles.inputShell}>
        {timeLeftLabel && lowMinutesAction ? (
          <View style={styles.lowMinutesAnchor} pointerEvents="box-none">
            <LowMinutesPill label={timeLeftLabel} action={lowMinutesAction.label} onPress={lowMinutesAction.onPress} />
          </View>
        ) : timeLeftLabel ? (
          <View style={styles.timeLeftAnchor} pointerEvents="none">
            <Text style={styles.timeLeftText}>{timeLeftLabel}</Text>
          </View>
        ) : null}
        {showChips ? (
          <View style={styles.suggestAnchor} testID="tutor-suggestions" onLayout={event => setChipsHeight(event.nativeEvent.layout.height)}>
            {/* owner 11 Oct, voice-first: with the microphone on, the learner just speaks; the hints say what to try */}
            {voiceLive ? <Text style={styles.trySaying}>Try Saying:</Text> : null}
            {suggestions.slice(0, 3).map(question => (
              <TouchableOpacity
                key={question}
                testID="tutor-suggestion"
                accessibilityRole="button"
                activeOpacity={0.85}
                onPress={() => {
                  pendingAsk.current = question;
                  setTextMessage(question);
                }}
                style={styles.suggestChip}>
                <Icon name="chat-question-outline" size={13} style={styles.suggestIcon} />
                <Text style={styles.suggestText}>{question}</Text>
              </TouchableOpacity>
            ))}
          </View>
        ) : null}
        {modeSwitch ? (
          <View style={styles.modeSwitchAnchor}>
            <TouchableOpacity
              testID="tutor-mode-switch"
              accessibilityRole="button"
              accessibilityLabel={modeSwitch.label}
              activeOpacity={0.85}
              onPress={modeSwitch.onPress}
              style={styles.modeSwitch}>
              <Icon name={modeSwitch.icon} size={13} style={styles.modeSwitchIcon} />
              <Text style={styles.modeSwitchText}>{modeSwitch.label}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        <StateTabMotion key={showMicPermissionWarning ? 'mic' : status}>
        {showMicPermissionWarning ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View
              style={[
                styles.permissionWarning,
                isCompactViewport && styles.permissionWarningCompact,
              ]}>
              <Icon
                name="microphone-off"
                size={isCompactViewport ? 13 : 14}
                style={styles.permissionWarningIcon}
              />
              <Text
                style={[
                  styles.permissionWarningText,
                  isCompactViewport && styles.permissionWarningTextCompact,
                ]}>
                {micOptional
                  ? 'Microphone is off. You can still type your reply.'
                  : 'Please allow microphone access and reload this tab for the best experience.'}
              </Text>
            </View>
          </View>
        ) : status === 'speaking' ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View style={[styles.speakingTab, VIVID && styles.tabVivid]}>
              <Icon name="account-voice" size={14} style={styles.speakingTabIcon} />
              <Text style={[styles.speakingTabText, VIVID && styles.tabTextVivid]}>Tutor is speaking…</Text>
            </View>
          </View>
        ) : status === 'connecting' ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View style={[styles.connectingTab, VIVID && styles.tabVivid]}>
              <Icon name="loading" size={14} style={styles.connectingTabIcon} />
              <Text style={[styles.connectingTabText, VIVID && styles.tabTextVivid]}>Connecting…</Text>
            </View>
          </View>
        ) : status === 'thinking' ? (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View style={[styles.thinkingTab, VIVID && styles.tabVivid]}>
              <Icon name="dots-horizontal" size={14} style={styles.thinkingTabIcon} />
              <Text style={[styles.thinkingTabText, VIVID && styles.tabTextVivid]}>Tutor is thinking…</Text>
            </View>
          </View>
        ) : (
          <View style={styles.promptTabAnchor} pointerEvents="none">
            <View
              style={[
                styles.promptTab,
                isCompactViewport && styles.promptTabCompact,
                VIVID && styles.tabVivid,
                VIVID && {borderColor: STATE_BORDER.prompt, backgroundColor: 'rgba(34,42,58,0.82)'},
              ]}>
              <LottieView
                source={require('../../utils/animations/mic-animation.json')}
                autoPlay
                loop
                colorFilters={PROMPT_TAB_LOTTIE_COLOR_FILTERS}
                style={[
                  styles.promptTabLottie,
                  isCompactViewport && styles.promptTabLottieCompact,
                  VIVID && styles.lottieVivid,
                ]}
              />
              <Text
                style={[
                  styles.promptTabText,
                  isCompactViewport && styles.promptTabTextCompact,
                  VIVID && styles.tabTextVivid,
                ]}>
                Your turn — type or speak your reply
              </Text>
            </View>
          </View>
        )}
        </StateTabMotion>
        <View
          style={[
            styles.inputBar,
            {minHeight: rem(TUTOR_INPUT_OUTER_HEIGHT_REM)},
            VIVID && {borderWidth: 2, borderColor: STATE_BORDER[showMicPermissionWarning ? 'prompt' : status] || STATE_BORDER.prompt},
          ]}>
          <TextInput
            // Use editable across platforms; on web we also pass `disabled` for safety.
            editable={!effectiveDisabled}
            {...(Platform.OS === 'web' ? { disabled: effectiveDisabled } : {})}
            style={styles.textInput}
            value={textMessage}
            onChangeText={text => {
              setTextMessage(text);
              if (onActivity) onActivity();
            }}
            placeholder="Type here to respond..."
            placeholderTextColor="rgba(246,251,255,0.56)"
            returnKeyType="send"
            multiline={false}
            blurOnSubmit={false}
            // Prefer onSubmitEditing when available; add web key handlers as belt-and-suspenders.
            onSubmitEditing={onSend}
            onKeyDown={Platform.OS === 'web' ? onKeyDown : undefined}
            onKeyPress={Platform.OS === 'web' ? onKeyPress : undefined}
          />
          <TouchableOpacity
            style={[
              styles.sendButton,
              isHoveredSend && styles.sendButtonHover,
              effectiveDisabled && styles.sendButtonDisabled,
            ]}
            onPress={onSend}
            disabled={effectiveDisabled}
            {...hoverProps}
          >
            <Icon
              name="send-outline"
              size={16}
              style={[
                styles.sendButtonIcon,
                isHoveredSend && styles.sendButtonIconHover,
                effectiveDisabled && styles.sendButtonIconDisabled,
              ]}
            />
            <Text
              style={[
                styles.sendButtonText,
                isHoveredSend && styles.sendButtonTextHover,
                effectiveDisabled && styles.sendButtonTextDisabled,
              ]}
            >
              Send Message
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    zIndex: 100,
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  inputShell: {
    width: '100%',
    maxWidth: TUTOR_INPUT_MAX_WIDTH,
    position: 'relative',
  },
  promptTabAnchor: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '100%',
    alignItems: 'center',
    zIndex: 3,
  },
  timeLeftAnchor: {
    position: 'absolute',
    right: 8,
    bottom: '100%',
    marginBottom: 6,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: 'rgba(16,21,31,0.72)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.28)',
    zIndex: 4,
  },
  timeLeftText: {
    color: '#C5E836',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
  tabVivid: { paddingHorizontal: 16, paddingVertical: 9, borderWidth: 1.5 },
  tabTextVivid: { fontSize: TYPE.sm },
  lottieVivid: { width: 22, height: 22 },
  suggestAnchor: {
    position: 'absolute',
    // wider than the bar so three hints fit on one row (owner round 14.2)
    left: -160,
    right: -160,
    bottom: '100%',
    marginBottom: 52,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    zIndex: 5,
  },
  suggestChip: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 4,
    marginTop: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: 'rgba(16,21,31,0.82)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.45)',
  },
  suggestIcon: { color: '#C5E836', marginRight: 6 },
  trySaying: { alignSelf: 'center', marginTop: 6, marginRight: 6, color: '#D7F46E', fontSize: TYPE.sm, fontFamily: FONTS.body, fontWeight: '700' },
  suggestText: { color: '#F6FBFF', fontSize: TYPE.sm, fontFamily: FONTS.body, fontWeight: '600' },
  modeSwitchAnchor: {
    position: 'absolute',
    left: 8,
    bottom: '100%',
    marginBottom: 6,
    zIndex: 4,
  },
  modeSwitch: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: 'rgba(16,21,31,0.72)',
    borderWidth: 1,
    borderColor: 'rgba(246,251,255,0.28)',
  },
  modeSwitchIcon: {
    color: '#F6FBFF',
    marginRight: 5,
  },
  modeSwitchText: {
    color: '#F6FBFF',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
  lowMinutesAnchor: {
    position: 'absolute',
    right: 8,
    bottom: '100%',
    marginBottom: 6,
    zIndex: 4,
  },
  promptTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.56)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(197,232,54,0.20)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  promptTabCompact: {
    maxWidth: 286,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  promptTabLottie: {
    width: 18,
    height: 18,
    marginRight: 6,
  },
  promptTabLottieCompact: {
    width: 16,
    height: 16,
    marginRight: 5,
  },
  promptTabText: {
    color: PROMPT_TAB_ACCENT_COLOR,
    fontWeight: '700',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
    letterSpacing: 0.2,
  },
  promptTabTextCompact: {
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
  },
  speakingTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(124,193,255,0.30)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  speakingTabIcon: {
    color: '#7CC1FF',
    marginRight: 6,
  },
  speakingTabText: {
    color: '#BFE0FF',
    fontWeight: '700',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
    letterSpacing: 0.2,
  },
  connectingTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(160,178,204,0.35)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  connectingTabIcon: {
    color: '#A9BBD4',
    marginRight: 6,
  },
  connectingTabText: {
    color: '#DCE6F4',
    fontWeight: '700',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
    letterSpacing: 0.2,
  },
  thinkingTab: {
    maxWidth: 340,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -1,
    backgroundColor: 'rgba(34,42,58,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,194,75,0.40)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  thinkingTabIcon: {
    color: '#FFC24B',
    marginRight: 6,
  },
  thinkingTabText: {
    color: '#FFE6B8',
    fontWeight: '700',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
    letterSpacing: 0.2,
  },
  permissionWarning: {
    maxWidth: 500,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    marginBottom: -1,
    backgroundColor: 'rgba(76,22,20,0.92)',
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: 'rgba(255,142,124,0.55)',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  permissionWarningCompact: {
    maxWidth: 360,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  permissionWarningIcon: {
    color: '#FFD0C8',
    marginRight: 6,
  },
  permissionWarningText: {
    flex: 1,
    color: '#FFE5DF',
    fontWeight: '700',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
    letterSpacing: 0.2,
  },
  permissionWarningTextCompact: {
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    lineHeight: 15,
  },
  inputBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(34,42,58,0.56)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.20)',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
  },
  textInput: {
    flex: 1,
    backgroundColor: 'rgba(9,12,20,0.45)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.20)',
    borderRadius: 11,
    paddingVertical: 11,
    paddingHorizontal: 12,
    color: 'rgba(246,251,255,0.96)',
    fontSize: TYPE.base,
    fontFamily: FONTS.body,
    ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}),
  },
  sendButton: {
    marginLeft: 10,
    paddingHorizontal: 12,
    paddingVertical: 11,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 11,
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.20)',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  sendButtonHover: {
    backgroundColor: 'rgba(197,232,54,0.92)',
    borderColor: 'rgba(197,232,54,0.92)',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  sendButtonIcon: {
    color: '#D7F46E',
    marginRight: 6,
  },
  sendButtonIconHover: {
    color: '#000',
  },
  sendButtonIconDisabled: {
    color: '#888',
  },
  sendButtonText: {
    color: '#D7F46E',
    fontWeight: '700',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
  },
  sendButtonTextHover: {
    color: '#000',
  },
  sendButtonTextDisabled: {
    color: '#888',
  },
});
